import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Plus, Edit3, Trash2, Save, X, Search, Filter, FileText, DollarSign, Calendar, MapPin, Tag, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const ContractManager = ({ calculationId, trialBalanceAccounts, onContractsUpdate }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm, setContractForm] = useState({
    contract_id: "",
    contract_name: "",
    contract_type: "cpff",
    customer_name: "",
    pop_start: "",
    pop_end: "",
    contract_value: "",
    funding_type: "government",
    contract_status: "active",
    prime_subcontractor: "prime",
    contract_description: "",
    direct_accounts: []
  });
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [taggedAccounts, setTaggedAccounts] = useState({});

  useEffect(() => { if (calculationId) { loadContracts(); loadTaggedAccounts(); } }, [calculationId]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contracts/${calculationId}`);
      setContracts(response.data.contracts || []);
      onContractsUpdate && onContractsUpdate(response.data.contracts || []);
    } catch (e) {
      if (e.response?.status !== 404) setError("Error loading contracts");
    } finally { setLoading(false); }
  };

  const loadTaggedAccounts = async () => {
    try {
      const response = await axios.get(`${API}/contract-account-mappings/${calculationId}`);
      const mappings = response.data.mappings || [];
      const grouped = {};
      mappings.forEach(mapping => {
        if (!grouped[mapping.contract_id]) grouped[mapping.contract_id] = [];
        grouped[mapping.contract_id].push(mapping);
      });
      setTaggedAccounts(grouped);
    } catch (e) {
      console.error("Error loading tagged accounts:", e);
    }
  };

  const handleSaveContract = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const contractData = {
        calculation_id: calculationId,
        contract_number: contractForm.contract_id,
        contract_name: contractForm.contract_name,
        contract_type: contractForm.contract_type,
        customer_name: contractForm.customer_name,
        start_date: contractForm.pop_start ? new Date(contractForm.pop_start).toISOString() : new Date().toISOString(),
        end_date: contractForm.pop_end ? new Date(contractForm.pop_end).toISOString() : new Date().toISOString(),
        contract_value: parseFloat(contractForm.contract_value) || 0,
        funding_type: contractForm.funding_type,
        contract_status: contractForm.contract_status,
        prime_subcontractor: contractForm.prime_subcontractor,
        contract_description: contractForm.contract_description
      };
      const response = editingContract
        ? await axios.put(`${API}/contracts/${editingContract.id}`, contractData)
        : await axios.post(`${API}/contracts`, contractData);
      if (response.data.success) {
        setSuccess(editingContract ? "Contract updated successfully!" : "Contract created successfully!");
        resetContractForm();
        await loadContracts();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving contract");
    } finally { setSaving(false); }
  };

  const handleEditContract = (contract) => {
    setContractForm({
      contract_id: contract.contract_number,
      contract_name: contract.contract_name,
      contract_type: contract.contract_type,
      customer_name: contract.customer_name,
      pop_start: contract.start_date?.substring(0, 10) || "",
      pop_end: contract.end_date?.substring(0, 10) || "",
      contract_value: contract.contract_value?.toString() || "",
      funding_type: contract.funding_type || "government",
      contract_status: contract.contract_status || "active",
      prime_subcontractor: contract.prime_subcontractor || "prime",
      contract_description: contract.contract_description || "",
      direct_accounts: contract.direct_accounts || []
    });
    setEditingContract(contract);
    setShowContractForm(true);
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm("Are you sure you want to delete this contract? This will also remove all associated account mappings.")) return;
    try {
      const res = await axios.delete(`${API}/contracts/${contractId}`);
      if (res.data.success) { setSuccess("Contract deleted successfully!"); await loadContracts(); setTimeout(()=>setSuccess(null),3000); }
    } catch (e) { setError(e.response?.data?.detail || "Error deleting contract"); }
  };

  const resetContractForm = () => {
    setContractForm({ contract_id: "", contract_name: "", contract_type: "cpff", customer_name: "", pop_start: "", pop_end: "", contract_value: "", funding_type: "government", contract_status: "active", prime_subcontractor: "prime", contract_description: "", direct_accounts: [] });
    setEditingContract(null); setShowContractForm(false);
  };

  // Helpers to normalize account shapes from various sources
  const pick = (obj, keys, fallback='') => {
    for (const k of keys) {
      if (obj && obj[k] != null && obj[k] !== '') return obj[k];
    }
    return fallback;
  };
  const parseAccountFields = (account) => {
    const raw = pick(account, ['account','ACCOUNT','Account'], '');
    let number = pick(account, ['account_number','ACCOUNT_NO','ACCOUNT_NUMBER','AccountNumber'], '');
    let name = pick(account, ['account_name','ACCOUNT_NAME','name','Name'], '');
    if (raw) {
      const parts = String(raw).split(' - ');
      if (!number) number = parts[0] || '';
      if (!name) name = parts.slice(1).join(' - ') || raw;
    }
    const net = Number(pick(account, ['net_amount','amount','AMOUNT','balance','BALANCE'], 0)) || 0;
    return { number: String(number), name: String(name), net };
  };

  const generateConsistentAccountId = (account) => {
    const { number, name, net } = parseAccountFields(account);
    const cleanName = name.replace(/[^a-zA-Z0-9]/g, '');
    const netPart = Math.abs(net).toString().replace('.', '');
    return `${number}_${cleanName}_${netPart}`;
  };

  const getAccountName = (account) => {
    const accountParts = account.account ? account.account.split(' - ') : ['', ''];
    return accountParts[1] || account.account || 'Unknown Account';
  };

  const getAccountNumber = (account) => {
    const accountParts = account.account ? account.account.split(' - ') : ['', ''];
    return accountParts[0] || 'N/A';
  };

  const handleTagAccount = async (account, contractId) => {
    try {
      const mappingData = {
        calculation_id: calculationId,
        contract_id: contractId,
        account_id: generateConsistentAccountId(account),
        account_name: getAccountName(account),
        account_number: getAccountNumber(account),
        net_amount: account.net_amount || 0,
        allocated_amount: account.net_amount || 0,
        allocation_percentage: 100,
        allocation_method: "direct"
      };
      const response = await axios.post(`${API}/contract-account-mappings`, mappingData);
      if (response.data.success) { setSuccess("Account tagged to contract successfully!"); await loadTaggedAccounts(); setTimeout(()=>setSuccess(null),3000); }
    } catch (e) {
      console.error("Error tagging account:", e);
      if (e.response?.data?.detail) setError(e.response.data.detail); else setError("Error tagging account");
    }
  };

  const handleUntagAccount = async (mappingId) => {
    try {
      const res = await axios.delete(`${API}/contract-account-mappings/${mappingId}`);
      if (res.data.success) { setSuccess("Account untagged successfully!"); await loadTaggedAccounts(); setTimeout(()=>setSuccess(null),3000); }
    } catch (e) { setError(e.response?.data?.detail || "Error untagging account"); }
  };

  const getFilteredAccounts = () => {
    const list = Array.isArray(trialBalanceAccounts) ? trialBalanceAccounts : [];
    if (list.length === 0) return [];
    // Normalize each item into a displayable object
    const norm = list.map(acc => {
      const raw = (acc.account ?? acc.ACCOUNT ?? acc.Account ?? '');
      let number = (acc.account_number ?? acc.ACCOUNT_NO ?? acc.ACCOUNT_NUMBER ?? acc.AccountNumber ?? '');
      let name = (acc.account_name ?? acc.ACCOUNT_NAME ?? acc.name ?? acc.Name ?? '');
      if (raw) {
        const parts = String(raw).split(' - ');
        if (!number) number = parts[0] || '';
        if (!name) name = parts.slice(1).join(' - ') || raw;
      }
      return { ...acc, __disp_account: `${number} - ${name}` };
    });
    return norm.filter(acc => (acc.__disp_account || '').toLowerCase().includes(accountSearchTerm.toLowerCase()));
  };

  const getAccountMappingInfo = (account) => {
    if (!selectedContract) return null;
    const contractMappings = taggedAccounts[selectedContract.id] || [];
    const accountId = generateConsistentAccountId(account);
    return contractMappings.find(mapping => mapping.account_id === accountId);
  };

  const formatCurrency = (value) => { if (typeof value !== 'number') return '$0.00'; return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value); };
  const getContractTypeLabel = (type) => ({ cpff: "Cost Plus Fixed Fee", ffp: "Firm Fixed Price", t_m: "Time & Materials" }[type] || type);
  const getContractStatusColor = (status) => ({ active: "bg-green-50 text-green-700 border-green-200", completed: "bg-blue-50 text-blue-700 border-blue-200", terminated: "bg-red-50 text-red-700 border-red-200" }[status] || "bg-slate-50 text-slate-700 border-slate-200");

  if (loading) return (<Card><CardContent className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div><p className="text-sm text-slate-600">Loading contracts...</p></CardContent></Card>);

  return (
    <div className="space-y-6">
      {error && (<Alert className="border-red-200 bg-red-50"><AlertCircle className="h-4 w-4 text-red-600" /><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>)}
      {success && (<Alert className="border-green-200 bg-green-50"><CheckCircle className="h-4 w-4 text-green-600" /><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>)}

      <Tabs defaultValue="contracts" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="contracts">Contract Management</TabsTrigger>
          <TabsTrigger value="accounts">Account Mapping</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2"><FileText className="h-5 w-5 text-indigo-600" /><span>Contract Management</span></CardTitle>
                <Button onClick={() => setShowContractForm(true)} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-2"/>Add Contract</Button>
              </div>
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium mb-2 text-slate-600">No Contracts Found</h3>
                  <p className="text-sm text-slate-500 mb-4">Add your first contract to begin contract-level cost allocation.</p>
                  <Button onClick={() => setShowContractForm(true)} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-2"/>Add First Contract</Button>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader><TableRow><TableHead>Contract ID</TableHead><TableHead>Contract Name</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Value</TableHead><TableHead>Status</TableHead><TableHead>POP</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {contracts.map((contract) => {
                        const directAccountsCount = taggedAccounts[contract.id]?.length || 0;
                        const totalDirectCosts = taggedAccounts[contract.id]?.reduce((sum, mapping) => sum + (mapping.direct_cost_amount || 0), 0) || 0;
                        return (
                          <TableRow key={contract.id}>
                            <TableCell className="font-medium">{contract.contract_number}</TableCell>
                            <TableCell><div><div className="font-medium">{contract.contract_name}</div>{directAccountsCount > 0 && (<div className="text-xs text-slate-500">{directAccountsCount} direct accounts • {formatCurrency(totalDirectCosts)}</div>)}</div></TableCell>
                            <TableCell>{contract.customer_name}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{getContractTypeLabel(contract.contract_type)}</Badge></TableCell>
                            <TableCell>{formatCurrency(contract.contract_value)}</TableCell>
                            <TableCell><Badge className={`text-xs ${getContractStatusColor(contract.contract_status || 'active')}`}>{contract.contract_status || 'active'}</Badge></TableCell>
                            <TableCell>{`${(contract.start_date||'').substring(0,10)} → ${(contract.end_date||'').substring(0,10)}`}</TableCell>
                            <TableCell>
                              <div className="flex gap-2"><Button size="sm" variant="outline" onClick={()=>handleEditContract(contract)}><Edit3 className="h-3 w-3 mr-1"/>Edit</Button><Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={()=>handleDeleteContract(contract.id)}><Trash2 className="h-3 w-3"/></Button></div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {showContractForm && (
            <Card>
              <CardHeader><CardTitle>{editingContract? 'Edit Contract' : 'Add Contract'}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div><Label>Contract Number</Label><Input value={contractForm.contract_id} onChange={(e)=>setContractForm({...contractForm, contract_id:e.target.value})}/></div>
                  <div><Label>Contract Name</Label><Input value={contractForm.contract_name} onChange={(e)=>setContractForm({...contractForm, contract_name:e.target.value})}/></div>
                  <div><Label>Customer</Label><Input value={contractForm.customer_name} onChange={(e)=>setContractForm({...contractForm, customer_name:e.target.value})}/></div>
                  <div><Label>Type</Label><Select value={contractForm.contract_type} onValueChange={(v)=>setContractForm({...contractForm, contract_type:v})}><SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger><SelectContent><SelectItem value="cpff">CPFF</SelectItem><SelectItem value="ffp">FFP</SelectItem><SelectItem value="t_m">T&amp;M</SelectItem></SelectContent></Select></div>
                  <div><Label>Value</Label><Input type="number" value={contractForm.contract_value} onChange={(e)=>setContractForm({...contractForm, contract_value:e.target.value})}/></div>
                  <div><Label>Start</Label><Input type="date" value={contractForm.pop_start} onChange={(e)=>setContractForm({...contractForm, pop_start:e.target.value})}/></div>
                  <div><Label>End</Label><Input type="date" value={contractForm.pop_end} onChange={(e)=>setContractForm({...contractForm, pop_end:e.target.value})}/></div>
                  <div><Label>Status</Label><Select value={contractForm.contract_status} onValueChange={(v)=>setContractForm({...contractForm, contract_status:v})}><SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="terminated">Terminated</SelectItem></SelectContent></Select></div>
                  <div><Label>Prime/Sub</Label><Select value={contractForm.prime_subcontractor} onValueChange={(v)=>setContractForm({...contractForm, prime_subcontractor:v})}><SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger><SelectContent><SelectItem value="prime">Prime</SelectItem><SelectItem value="subcontractor">Subcontractor</SelectItem></SelectContent></Select></div>
                  <div className="md:col-span-3"><Label>Description</Label><Input value={contractForm.contract_description} onChange={(e)=>setContractForm({...contractForm, contract_description:e.target.value})}/></div>
                </div>
                <div className="flex justify-end gap-2"><Button variant="outline" onClick={()=>{setShowContractForm(false); setEditingContract(null);}}><X className="h-4 w-4 mr-1"/>Cancel</Button><Button onClick={handleSaveContract} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"><Save className="h-4 w-4 mr-1"/>{saving? 'Saving...' : 'Save Contract'}</Button></div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="accounts" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Map Direct Accounts to Contract</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className="md:col-span-2"><Input placeholder="Search accounts" value={accountSearchTerm} onChange={(e)=>setAccountSearchTerm(e.target.value)}/></div>
                <div>
                  <Select value={selectedContract?.id || ''} onValueChange={(id)=>{const c = contracts.find(x=>x.id===id); setSelectedContract(c);}}>
                    <SelectTrigger><SelectValue placeholder="Select contract"/></SelectTrigger>
                    <SelectContent>
                      {contracts.map(c => (<SelectItem key={c.id} value={c.id}>{c.contract_number} — {c.contract_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedContract ? (
                <div className="rounded-md border divide-y max-h-[420px] overflow-y-auto">
                  {getFilteredAccounts().map((acc, idx) => {
                    const info = getAccountMappingInfo(acc);
                    return (
                      <div key={idx} className="p-3 flex items-center justify-between hover:bg-slate-50">
                        <div className="mr-2 min-w-0">
                          <div className="truncate font-medium text-sm">{acc.account}</div>
                          {info && (<div className="text-xs text-slate-500">Tagged</div>)}
                        </div>
                        <div className="flex gap-2">
                          {!info ? (
                            <Button size="sm" onClick={()=>handleTagAccount(acc, selectedContract.id)}>Tag</Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={()=>handleUntagAccount(info.id)}>Untag</Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-slate-500">Select a contract to tag accounts</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContractManager;